import { createClient } from '@/prismicio';
import { asText, Content, filter } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import {
  BlogDocument,
  FeaturedPostsSliceDefault,
  FeaturedPostsSliceLastThreePosts,
} from '../../../prismicio-types';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import Link from 'next/link';
import BlogCard from '@/components/blogHome/BlogCard';
import { blogCardQuery } from '@/components/blogHome/constants';

enum SLICE_VARIATION {
  default = 'default',
  lastThreePosts = 'lastThreePosts',
}

/**
 * Props for `FeaturedPosts`.
 */
export type FeaturedPostsProps =
  SliceComponentProps<Content.FeaturedPostsSlice>;

/**
 * Component for "FeaturedPosts" Slices.
 */
const FeaturedPosts = async ({ slice }: FeaturedPostsProps) => {
  const client = createClient();

  const blogs = await client.getAllByType('blog', {
    orderings: [
      { field: 'document.first_publication_date', direction: 'desc' },
    ],
    filters: [filter.at('my.blog.featured', true)],
    limit: 4,
    graphQuery: blogCardQuery,
  });

  const blogsToShow =
    slice.variation === SLICE_VARIATION.lastThreePosts
      ? blogs.slice(0, 3)
      : blogs;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      {slice.variation === SLICE_VARIATION.default && (
        <NormalFeaturedPosts blogs={blogs} slice={slice} />
      )}
      {slice.variation === SLICE_VARIATION.lastThreePosts && (
        <HighlightedFeaturedPosts blogs={blogsToShow} slice={slice} />
      )}
    </section>
  );
};

const NormalFeaturedPosts = ({
  blogs,
  slice,
}: {
  blogs: BlogDocument[];
  slice: FeaturedPostsSliceDefault;
}): JSX.Element => {
  const {
    primary: { title, button_label: buttonLabel, button_link: buttonLink },
  } = slice;
  return (
    <div className="max-w-screen-2xl mx-auto px-5 md:px-16 py-16 lg:py-28">
      <div className="text-white flex flex-col gap-y-8">
        <PrismicRichText field={title} />
        <PrismicNextLink field={buttonLink} className="button-outline-white">
          <span>{buttonLabel}</span>
        </PrismicNextLink>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {blogs.map(({ id, data, uid }) => (
            <Link key={id} href={`/blog/${uid}`}>
              <div className="flex flex-col gap-y-6 group">
                <PrismicNextImage
                  field={data.image}
                  className="rounded-[20px]"
                />
                <h3
                  className="text-h5 group-hover:underline overflow-hidden"
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {asText(data.card_title)}
                </h3>
                <span
                  className="overflow-hidden"
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {data.meta_description}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

const HighlightedFeaturedPosts = ({
  blogs,
  slice,
}: {
  blogs: BlogDocument[];
  slice: FeaturedPostsSliceLastThreePosts;
}): JSX.Element => {
  const {
    primary: { title },
  } = slice;
  return (
    <div className="bg-black">
      <div className="max-w-screen-2xl mx-auto flex flex-col gap-y-12 lg:gap-y-20 px-5 md:px-16 py-16 lg:py-28">
        <div className="text-white">
          <PrismicRichText field={title} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {blogs.map(({ data, uid, last_publication_date }) => (
            <BlogCard
              key={`feature_blog_${uid}`}
              {...data}
              cardType="featured"
              uid={uid}
              containerClass="rounded-[20px] bg-white-gradient text-black"
              date={last_publication_date}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FeaturedPosts;
